/**
 * Client-side idempotency key generation.
 * Produces deterministic keys for critical operations to prevent
 * duplicate submissions on retry.
 */

/**
 * Generate a deterministic idempotency key for avatar generation.
 * Same inputs within the same time window produce the same key.
 */
export function makeAvatarIdempotencyKey(params: {
  userId?: string;
  tenantId: string;
  styleId: string;
  subStyleId?: string | null;
  themeId?: string | null;
  selfieStoragePath: string;
  /**
   * Scopes the key to ONE batch of work. Without it, a reorder or a regenerate
   * with the same selfie + style replays the PREVIOUS order's server-side
   * idempotency entry ("Idempotency hit (completed)") and returns stale
   * generation ids instead of running the model again.
   */
  batchKey?: string;
}): string {
  const parts = [
    "gen",
    params.userId || "anon",
    params.tenantId,
    params.styleId,
    params.subStyleId || "_",
    params.themeId || "_",
    params.selfieStoragePath,
    params.batchKey || "_",
  ];
  return parts.join(":");
}


/**
 * Generate a deterministic idempotency key for card submission.
 */
export function makeSubmitCardIdempotencyKey(params: {
  userId?: string;
  tenantId: string;
  style: string;
  cardColor: string;
  firstName: string;
  lastName: string;
}): string {
  // Include a timestamp bucket (10-minute window) to allow re-submissions
  // after a reasonable cooldown, while preventing rapid duplicates.
  const timeBucket = Math.floor(Date.now() / (10 * 60 * 1000));
  const parts = [
    "submit",
    params.userId || "anon",
    params.tenantId,
    params.style,
    params.cardColor,
    params.firstName,
    params.lastName,
    String(timeBucket),
  ];
  return parts.join(":");
}
